import React, { useEffect, useState } from 'react';
import { Users, Circle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { toTitleCase } from '@/lib/text-utils';

interface OnlineUser {
  user_id: string;
  user_email: string;
  user_name: string;
  company_id: string;
  company_name: string;
  last_seen: string;
  status: 'online' | 'idle' | 'offline';
}

export const UserPresenceIndicator: React.FC = () => {
  const { user, selectedCompany } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!user || !selectedCompany) return;

    const channel = supabase.channel(`company_${selectedCompany.id}_presence`);

    // Track current user presence
    const trackPresence = async () => {
      const rawName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuario';
      const presenceData = {
        user_id: user.id,
        user_email: user.email,
        user_name: toTitleCase(rawName),
        company_id: selectedCompany.id,
        company_name: selectedCompany.name,
        last_seen: new Date().toISOString(),
        status: 'online'
      };

      await channel.track(presenceData);
    };

    // Listen to presence changes
    channel
      .on('presence', { event: 'sync' }, () => {
        const presenceState = channel.presenceState<OnlineUser>();
        
        // Deduplicate users: keep only the most recent presence per user_id + company_id
        const userMap = new Map<string, OnlineUser>();
        
        Object.values(presenceState).forEach(presence => {
          presence.forEach(presenceUser => {
            const key = `${presenceUser.user_id}-${presenceUser.company_id}`;
            const existing = userMap.get(key);
            
            // Keep the most recent presence
            if (!existing || new Date(presenceUser.last_seen) > new Date(existing.last_seen)) {
              userMap.set(key, presenceUser);
            }
          });
        });

        // Convert map to array and filter out current user
        const uniqueUsers = Array.from(userMap.values()).filter(u => u.user_id !== user.id);
        setOnlineUsers(uniqueUsers);
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        // Solo log en desarrollo para evitar spam en consola
        if (process.env.NODE_ENV === 'development') {
          console.log('User joined:', newPresences);
        }
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        // Solo log en desarrollo para evitar spam en consola
        if (process.env.NODE_ENV === 'development') {
          console.log('User left:', leftPresences);
        }
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await trackPresence();
        } else if (status === 'CHANNEL_ERROR') {
          console.warn('Presence channel connection error - will retry automatically');
        } else if (status === 'TIMED_OUT') {
          console.warn('Presence channel timed out - will retry automatically');
        }
      });

    // Update presence every 2 minutes (menos frecuente para evitar loops)
    const presenceInterval = setInterval(() => {
      // Solo actualizar si hay actividad reciente
      if (document.visibilityState === 'visible') {
        trackPresence();
      }
    }, 2 * 60 * 1000); // 2 minutos

    // Update status to idle after 5 minutes of inactivity
    let idleTimeout: NodeJS.Timeout;
    
    const resetIdleTimer = () => {
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => {
        const rawName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuario';
        channel.track({
          user_id: user.id,
          user_email: user.email,
          user_name: toTitleCase(rawName),
          company_id: selectedCompany.id,
          company_name: selectedCompany.name,
          last_seen: new Date().toISOString(),
          status: 'idle'
        });
      }, 5 * 60 * 1000); // 5 minutes
    };

    // Event listeners deshabilitados temporalmente para evitar reinicios
    // const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    // events.forEach(event => {
    //   document.addEventListener(event, resetIdleTimer, true);
    // });

    resetIdleTimer();

    return () => {
      clearInterval(presenceInterval);
      clearTimeout(idleTimeout);
      // events.forEach(event => {
      //   document.removeEventListener(event, resetIdleTimer, true);
      // });
      supabase.removeChannel(channel);
    };
  }, [user, selectedCompany]);

  if (!selectedCompany || onlineUsers.length === 0) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-status-success';
      case 'idle': return 'text-warning';
      case 'offline': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online': return 'En línea';
      case 'idle': return 'Ausente';
      case 'offline': return 'Desconectado';
      default: return 'Desconocido';
    }
  };

  const formatLastSeen = (lastSeen: string) => {
    const diff = Date.now() - new Date(lastSeen).getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `Hace ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Hace ${hours}h`;
    const days = Math.floor(hours / 24);
    return `Hace ${days}d`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-8 w-8 p-0 hover:bg-accent/50"
        >
          <Users className="h-4 w-4 text-muted-foreground" />
          {onlineUsers.length > 0 && (
            <Badge 
              variant="secondary" 
              className="absolute -top-1 -right-1 h-4 w-4 p-0 text-xs flex items-center justify-center min-w-0 bg-primary text-primary-foreground"
            >
              {onlineUsers.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent 
        className="w-80 p-0" 
        align="end"
        sideOffset={4}
      >
        <div className="p-4 border-b">
          <h3 className="font-semibold text-sm">
            Usuarios Conectados en {selectedCompany.name}
          </h3>
          <p className="text-xs text-muted-foreground">
            {onlineUsers.length} usuario{onlineUsers.length !== 1 ? 's' : ''} en línea
          </p>
        </div>

        <div className="max-h-64 overflow-y-auto">
          {onlineUsers.map((onlineUser) => (
            <div 
              key={`${onlineUser.user_id}-${onlineUser.company_id}`}
              className="flex items-center space-x-3 p-3 hover:bg-accent/50 transition-colors"
            >
              <div className="relative">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" alt={onlineUser.user_name} />
                  <AvatarFallback className="text-xs bg-primary/10 text-primary">
                    {onlineUser.user_name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Circle 
                  className={cn(
                    "absolute -bottom-0.5 -right-0.5 h-3 w-3 fill-current",
                    getStatusColor(onlineUser.status)
                  )}
                />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {toTitleCase(onlineUser.user_name)}
                </p>
                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                  <span className={getStatusColor(onlineUser.status)}>
                    {getStatusText(onlineUser.status)}
                  </span>
                  <span>•</span>
                  <span>{formatLastSeen(onlineUser.last_seen)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {onlineUsers.length === 0 && (
          <div className="p-6 text-center text-muted-foreground text-sm">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No hay otros usuarios conectados</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};